import { z } from "zod";
import { sheetColumnSchema } from "./google";
/**
 * Contratos compartilhados entre API e frontend.
 *
 * Uma definição só: o schema valida a entrada no servidor e tipa o cliente.
 * DTO duplicado nos dois lados sai de sincronia — e o sintoma é sempre o
 * mesmo, um campo que o front manda e o back ignora em silêncio.
 */
// ── Comuns ──────────────────────────────────────────────────────────────────
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    // Teto obrigatório: sem ele, `pageSize=100000` é uma varredura de tabela
    // que qualquer usuário autenticado dispara.
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * Booleano vindo da query string.
 *
 * `z.coerce.boolean()` NÃO serve aqui: ele aplica `Boolean(valor)`, e
 * `Boolean("false")` é `true`. Todo filtro escrito com ele responde "sim" para
 * qualquer valor não vazio — inclusive para o "não" que a pessoa escolheu.
 */
export const queryBoolean = z
    .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
    .transform((v) => v === true || v === "true" || v === "1");
export const errorBodySchema = z.object({
    statusCode: z.number(),
    message: z.string(),
    errors: z.array(z.object({ field: z.string(), message: z.string() })).optional(),
    correlationId: z.string().optional(),
    timestamp: z.string(),
    path: z.string(),
});
// ── Autenticação ────────────────────────────────────────────────────────────
export const APP_ROLES = [
    "super_admin",
    "admin",
    "manager",
    "sales",
    "reception",
    "professional",
];
export const appRoleSchema = z.enum(APP_ROLES);
export const loginSchema = z.object({
    email: z.string().email("E-mail inválido"),
    password: z.string().min(1, "Informe a senha"),
});
export const registerSchema = z.object({
    email: z.string().email("E-mail inválido"),
    // Comprimento em vez de composição, conforme NIST SP 800-63B: regras de
    // caractere produzem senhas previsíveis sem ganho real de entropia.
    password: z.string().min(12, "A senha precisa de ao menos 12 caracteres").max(200),
    fullName: z.string().min(2, "Informe o nome completo").max(120),
    companyName: z.string().max(120).optional(),
    /**
     * Template de partida do tenant: `aesthetics` (padrão), `generic` ou
     * `infoproduto`. Só define pipeline, etiquetas e automações-modelo iniciais.
     */
    vertical: z.enum(["aesthetics", "generic", "infoproduto"]).optional(),
});
export const authTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
});
export const sessionUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    tenantId: z.string().uuid().nullable(),
    roles: z.array(appRoleSchema),
    /** Dono da plataforma (`PLATFORM_ADMIN_EMAILS`). Vem do `/auth/me`. */
    isPlatformAdmin: z.boolean().optional(),
    /** Sessão de suporte em andamento, quando o token é de suporte. */
    support: z
        .object({
        sessionId: z.string(),
        expiresAt: z.string(),
        tenantName: z.string().nullable(),
    })
        .nullable()
        .optional(),
});
// ── Atribuição ──────────────────────────────────────────────────────────────
/**
 * Identificadores de tracking.
 *
 * Cada um tem campo próprio: `ctwa_clid`, `fbclid`, `fbc`, `fbp` e `gclid` têm
 * origens e semânticas diferentes, e um campo genérico `click_id` tornaria
 * impossível reconstruir de onde a pessoa veio.
 */
export const attributionSchema = z.object({
    platform: z.string().max(40).optional(),
    source: z.string().max(60).optional(),
    medium: z.string().max(60).optional(),
    channel: z.string().max(60).optional(),
    campaignExternalId: z.string().max(64).optional(),
    campaignName: z.string().max(200).optional(),
    adsetExternalId: z.string().max(64).optional(),
    adsetName: z.string().max(200).optional(),
    adExternalId: z.string().max(64).optional(),
    adName: z.string().max(200).optional(),
    utmSource: z.string().max(120).optional(),
    utmMedium: z.string().max(120).optional(),
    utmCampaign: z.string().max(200).optional(),
    utmContent: z.string().max(200).optional(),
    utmTerm: z.string().max(200).optional(),
    fbclid: z.string().max(500).optional(),
    fbc: z.string().max(500).optional(),
    fbp: z.string().max(500).optional(),
    gclid: z.string().max(500).optional(),
    ctwaClid: z.string().max(500).optional(),
    landingPage: z.string().max(500).optional(),
    referrer: z.string().max(500).optional(),
});
// ── Leads ───────────────────────────────────────────────────────────────────
export const LEAD_STATUSES = [
    "new",
    "working",
    "qualified",
    "unqualified",
    "converted",
    "lost",
];
export const LEAD_TEMPERATURES = ["cold", "warm", "hot", "very_hot"];
export const leadStatusSchema = z.enum(LEAD_STATUSES);
export const leadTemperatureSchema = z.enum(LEAD_TEMPERATURES);
export const createLeadSchema = z
    .object({
    fullName: z.string().trim().min(2).max(160),
    phone: z.string().trim().max(30).optional(),
    email: z.string().email().optional(),
    source: z.string().trim().max(60).default("manual"),
    sourceDetail: z.string().trim().max(160).optional(),
    attribution: attributionSchema.optional(),
})
    // Lead sem forma de contato não é acionável e nunca casa com ninguém na
    // deduplicação, criando um contato órfão a cada envio.
    .refine((v) => Boolean(v.phone ?? v.email), {
    message: "Informe ao menos telefone ou e-mail",
    path: ["phone"],
});
/**
 * Edição de lead.
 *
 * Patch parcial de verdade: campo ausente significa "não mexa", e `ownerId:
 * null` significa "tire o dono". Sem essa distinção a interface precisaria
 * reenviar o objeto inteiro a cada alteração — e todo campo que ela ainda não
 * conhecesse seria apagado no caminho.
 */
export const updateLeadSchema = z
    .object({
    status: leadStatusSchema.optional(),
    temperature: leadTemperatureSchema.optional(),
    ownerId: z.string().uuid().nullable().optional(),
    lostReason: z.string().trim().min(3).max(200).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" })
    // Perda sem motivo é um dado que não serve para nada: o relatório de
    // motivos vira uma barra "(vazio)" maior que todas as outras, e a pergunta
    // "por que estamos perdendo?" fica sem resposta justamente onde ela importa.
    .refine((v) => v.status !== "lost" || Boolean(v.lostReason), {
    message: "Informe o motivo da perda",
    path: ["lostReason"],
});
/**
 * Sugestões de motivo de perda.
 *
 * Sugestão e não enum: cada negócio perde por razões próprias, e uma lista
 * fechada empurraria metade das perdas para "Outro" — que é o mesmo que não
 * registrar. O campo aceita texto livre; isto só evita que dez pessoas
 * escrevam "preço", "Preço alto" e "caro" para a mesma coisa.
 */
export const LOST_REASON_SUGGESTIONS = [
    "Preço",
    "Sem resposta",
    "Fora da região",
    "Escolheu concorrente",
    "Não é o momento",
    "Contato inválido",
];
// ── Período ─────────────────────────────────────────────────────────────────
/**
 * Atalhos de período — os mesmos que o servidor resolve (`resolvePeriod`, no
 * core). A tela só escolhe o atalho; quem transforma em datas é o servidor,
 * no fuso da clínica, e devolve as datas para a tela mostrar.
 *
 * "Últimos N dias" são os dias completos até ontem, como no Gerenciador de
 * Anúncios. "Este mês" conta hoje.
 */
export const PERIOD_PRESETS = [
    "today",
    "yesterday",
    "last_7d",
    "last_14d",
    "last_30d",
    "last_90d",
    "this_month",
    "last_month",
    "all",
    "custom",
];
export const PERIOD_PRESET_LABEL = {
    today: "Hoje",
    yesterday: "Ontem",
    last_7d: "Últimos 7 dias",
    last_14d: "Últimos 14 dias",
    last_30d: "Últimos 30 dias",
    last_90d: "Últimos 90 dias",
    this_month: "Este mês",
    last_month: "Mês passado",
    all: "Todo o período",
    custom: "Personalizado",
};
/** O que a tela abre quando ninguém escolheu nada ainda. */
export const DEFAULT_PERIOD_PRESET = "this_month";
const diaIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a data no formato AAAA-MM-DD");
/**
 * Campos de período aceitos por toda rota que filtra por data.
 *
 * `from`/`to` são DIAS (AAAA-MM-DD, inclusivos) e só valem no personalizado.
 * `days` é o formato antigo — últimos N dias contando hoje — e continua
 * aceito para quem ainda chama assim. Período inválido volta como 400 com o
 * motivo, nunca trocado por outro em silêncio.
 */
export const periodQueryFields = {
    preset: z.enum(PERIOD_PRESETS).optional(),
    from: diaIso.optional(),
    to: diaIso.optional(),
    days: z.coerce.number().int().min(1).max(730).optional(),
};
export const periodQuerySchema = z.object(periodQueryFields);
export const listLeadsQuerySchema = paginationSchema.extend({
    search: z.string().trim().min(1).max(120).optional(),
    status: z.array(leadStatusSchema).optional(),
    temperature: z.array(leadTemperatureSchema).optional(),
    source: z.array(z.string()).optional(),
    ownerId: z.string().uuid().optional(),
    unanswered: queryBoolean.optional(),
    createdFrom: z.coerce.date().optional(),
    createdTo: z.coerce.date().optional(),
    /** Período pelo dia de entrada do lead, no fuso da clínica. Sem ele, todos. */
    ...periodQueryFields,
});
// ── Notas e atividades manuais ──────────────────────────────────────────────
/**
 * O que o atendente registra à mão.
 *
 * Lista fechada, ao contrário do motivo de perda: aqui o valor está justamente
 * em poder contar "quantas ligações foram feitas" — e isso só funciona se
 * "ligação", "Ligação" e "liguei" forem o mesmo registro. O texto livre vai
 * no corpo.
 */
export const MANUAL_ACTIVITY_TYPES = ["call", "whatsapp", "email", "visit", "other"];
export const manualActivityTypeSchema = z.enum(MANUAL_ACTIVITY_TYPES);
export const MANUAL_ACTIVITY_LABEL = {
    call: "Ligação",
    whatsapp: "WhatsApp",
    email: "E-mail",
    visit: "Visita",
    other: "Outro",
};
/**
 * Tipos que contam como resposta ao lead.
 *
 * `other` fica de fora: é a caixa de "qualquer coisa", e deixá-lo marcar o SLA
 * transformaria uma anotação interna em "atendemos em 3 minutos".
 */
export const OUTBOUND_ACTIVITY_TYPES = [
    "call",
    "whatsapp",
    "email",
    "visit",
];
export const createActivitySchema = z.object({
    type: manualActivityTypeSchema,
    body: z.string().trim().min(1, "Descreva o que aconteceu").max(2000),
    /** Quando aconteceu de fato — o registro costuma vir depois do ato. */
    occurredAt: z.coerce.date().optional(),
});
export const createNoteSchema = z.object({
    body: z.string().trim().min(1, "Escreva a nota").max(4000),
});
// ── Etiquetas ───────────────────────────────────────────────────────────────
/**
 * Paleta fechada.
 *
 * Um seletor de cor livre produz etiquetas ilegíveis — amarelo claro sobre
 * branco — e nenhuma consistência entre quem cadastrou em janeiro e quem
 * cadastrou em junho. Seis tons conferidos contra fundo claro e escuro
 * resolvem o caso real, que é distinguir grupos de etiquetas à distância.
 */
export const TAG_COLORS = ["slate", "rose", "amber", "emerald", "sky", "violet"];
export const tagColorSchema = z.enum(TAG_COLORS);
const tagName = z
    .string()
    .trim()
    .min(2, "A etiqueta precisa de ao menos 2 caracteres")
    .max(40, "Etiqueta muito longa")
    // Espaço duplo vira simples: "Botox  Full" e "Botox Full" são a mesma
    // etiqueta para quem lê, e viram duas no banco se não normalizarmos.
    .transform((v) => v.replace(/\s+/g, " "));
export const createTagSchema = z.object({
    name: tagName,
    color: tagColorSchema.optional(),
});
export const updateTagSchema = z
    .object({
    name: tagName.optional(),
    color: tagColorSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
/**
 * Marcar o lead: por id de etiqueta existente ou por nome.
 *
 * O nome existe para o caso da ficha, onde o atendente digita "Retorno" sem
 * saber se a etiqueta já foi criada. Exigir a criação antes transformaria um
 * gesto em duas telas, e é assim que etiqueta deixa de ser usada.
 */
export const attachTagSchema = z
    .object({
    tagId: z.string().uuid().optional(),
    name: tagName.optional(),
    color: tagColorSchema.optional(),
})
    .refine((v) => Boolean(v.tagId ?? v.name), {
    message: "Informe a etiqueta",
    path: ["name"],
});
// ── Campos customizados ─────────────────────────────────────────────────────
export const CUSTOM_FIELD_TYPES = [
    "text",
    "number",
    "currency",
    "date",
    "datetime",
    "boolean",
    "select",
    "multiselect",
    "phone",
    "email",
    "url",
];
export const CUSTOM_FIELD_ENTITIES = ["lead", "contact"];
export const customFieldTypeSchema = z.enum(CUSTOM_FIELD_TYPES);
export const customFieldEntitySchema = z.enum(CUSTOM_FIELD_ENTITIES);
export const CUSTOM_FIELD_TYPE_LABEL = {
    text: "Texto",
    number: "Número",
    currency: "Moeda",
    date: "Data",
    datetime: "Data e hora",
    boolean: "Sim/Não",
    select: "Escolha única",
    multiselect: "Escolha múltipla",
    phone: "Telefone",
    email: "E-mail",
    url: "Endereço web",
};
export const CUSTOM_FIELD_ENTITY_LABEL = {
    lead: "Lead",
    contact: "Contato",
};
/** Tipos que exigem lista de opções — sem ela o campo não é preenchível. */
export const CHOICE_FIELD_TYPES = ["select", "multiselect"];
const needsOptions = (v) => !v.fieldType || !CHOICE_FIELD_TYPES.includes(v.fieldType) || (v.options?.length ?? 0) > 0;
export const createCustomFieldSchema = z
    .object({
    entity: customFieldEntitySchema,
    label: z.string().trim().min(2, "Informe o rótulo").max(80),
    fieldType: customFieldTypeSchema.default("text"),
    options: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
    required: z.boolean().optional(),
    /** Dado sensível (LGPD art. 11): leitura restrita, fora de export e log. */
    sensitive: z.boolean().optional(),
    position: z.number().int().min(0).max(999).optional(),
})
    .refine(needsOptions, { message: "Informe ao menos uma opção", path: ["options"] });
/**
 * Edição do campo.
 *
 * `key` e `entity` não entram: a chave é o identificador estável que o
 * mapeamento de formulário da Meta (fase 4.4) vai usar, e trocá-la órfã todos
 * os valores já gravados. O rótulo muda à vontade — é ele que aparece na tela.
 */
export const updateCustomFieldSchema = z
    .object({
    label: z.string().trim().min(2).max(80).optional(),
    fieldType: customFieldTypeSchema.optional(),
    options: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
    required: z.boolean().optional(),
    sensitive: z.boolean().optional(),
    position: z.number().int().min(0).max(999).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" })
    .refine(needsOptions, { message: "Informe ao menos uma opção", path: ["options"] });
/**
 * Gravação de valores: `{ chave: valor }`.
 *
 * O valor chega como o `<input>` produziu — string, quase sempre — e é o
 * servidor que converte conforme o tipo do campo. Validar aqui exigiria que o
 * schema conhecesse a definição de cada campo do tenant, que só existe no
 * banco.
 */
export const setCustomFieldValuesSchema = z.record(z.string(), z.unknown());
// ── Tarefas ─────────────────────────────────────────────────────────────────
export const TASK_STATUSES = ["pending", "done", "cancelled"];
export const taskStatusSchema = z.enum(TASK_STATUSES);
export const TASK_STATUS_LABEL = {
    pending: "Pendente",
    done: "Concluída",
    cancelled: "Cancelada",
};
/**
 * Recortes de prazo.
 *
 * Calculados no fuso do tenant, nunca em UTC: uma tarefa marcada para as 22h
 * de terça cairia em quarta num servidor UTC e sumiria do filtro "hoje" de
 * quem a criou.
 */
export const TASK_DUE_FILTERS = ["overdue", "today", "week", "none"];
export const taskDueFilterSchema = z.enum(TASK_DUE_FILTERS);
export const TASK_DUE_LABEL = {
    overdue: "Atrasadas",
    today: "Hoje",
    week: "Próximos 7 dias",
    none: "Sem prazo",
};
export const createTaskSchema = z.object({
    title: z.string().trim().min(2, "Descreva a tarefa").max(160),
    description: z.string().trim().max(2000).optional(),
    dueAt: z.coerce.date().optional(),
    assigneeId: z.string().uuid().nullable().optional(),
    leadId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
});
export const updateTaskSchema = z
    .object({
    title: z.string().trim().min(2).max(160).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    dueAt: z.coerce.date().nullable().optional(),
    assigneeId: z.string().uuid().nullable().optional(),
    status: taskStatusSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const listTasksQuerySchema = paginationSchema.extend({
    /** `mine` é o padrão: a tela de tarefas é pessoal antes de ser gerencial. */
    scope: z.enum(["mine", "all", "unassigned"]).default("mine"),
    status: taskStatusSchema.optional(),
    due: taskDueFilterSchema.optional(),
    leadId: z.string().uuid().optional(),
});
// ── Distribuição de leads ───────────────────────────────────────────────────
export const ROUTING_STRATEGIES = [
    "round_robin",
    "least_load",
    "specialty",
    "working_hours",
    "manual",
];
export const routingStrategySchema = z.enum(ROUTING_STRATEGIES);
export const ROUTING_STRATEGY_LABEL = {
    round_robin: "Rodízio",
    least_load: "Menor carga",
    specialty: "Por especialidade",
    working_hours: "Quem está no expediente",
    manual: "Não distribuir",
};
export const ROUTING_STRATEGY_HINT = {
    round_robin: "Um por vez, na ordem. O cursor sobrevive a reinício do sistema.",
    least_load: "Quem tem menos leads em aberto agora.",
    specialty: "Casa o interesse do lead com as especialidades da equipe.",
    working_hours: "Só quem está dentro do expediente configurado.",
    manual: "O lead fica na fila de não atribuídos.",
};
/** Mesmos operadores da pontuação e da automação — um avaliador só. */
export const CONDITION_OPERATORS = [
    "eq",
    "neq",
    "contains",
    "in",
    "not_in",
    "gt",
    "gte",
    "lt",
    "lte",
    "exists",
    "not_exists",
];
export const conditionOperatorSchema = z.enum(CONDITION_OPERATORS);
export const CONDITION_OPERATOR_LABEL = {
    eq: "é igual a",
    neq: "é diferente de",
    contains: "contém",
    in: "está entre",
    not_in: "não está entre",
    gt: "é maior que",
    gte: "é maior ou igual a",
    lt: "é menor que",
    lte: "é menor ou igual a",
    exists: "está preenchido",
    not_exists: "está vazio",
};
/** Campos que a regra consegue ler do lead que acabou de chegar. */
export const ROUTING_FIELDS = [
    { value: "source", label: "Origem" },
    { value: "attribution.campaignName", label: "Campanha" },
    { value: "attribution.adName", label: "Anúncio" },
    { value: "attribution.platform", label: "Plataforma" },
    { value: "attribution.utmSource", label: "utm_source" },
    { value: "attribution.ctwaClid", label: "Veio de Click-to-WhatsApp" },
    { value: "customFields.procedure_interest", label: "Procedimento de interesse" },
    { value: "customFields.urgency", label: "Urgência" },
];
export const routingConditionSchema = z.object({
    field: z.string().trim().min(1).max(120),
    operator: conditionOperatorSchema,
    value: z.unknown().optional(),
});
export const createRoutingRuleSchema = z.object({
    name: z.string().trim().min(2, "Dê um nome à regra").max(80),
    strategy: routingStrategySchema.default("round_robin"),
    priority: z.number().int().min(0).max(999).optional(),
    conditions: z.array(routingConditionSchema).max(10).optional(),
    teamId: z.string().uuid().nullable().optional(),
    userIds: z.array(z.string().uuid()).max(50).optional(),
    active: z.boolean().optional(),
});
export const updateRoutingRuleSchema = createRoutingRuleSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
// ── Contatos ────────────────────────────────────────────────────────────────
export const listContactsQuerySchema = paginationSchema.extend({
    search: z.string().trim().min(1).max(120).optional(),
    /** `true` filtra quem já comprou; `false`, quem ainda não. */
    isCustomer: queryBoolean.optional(),
});
// ── Pipelines e etapas ──────────────────────────────────────────────────────
/** Mesma paleta das etiquetas: seis tons conferidos nos dois temas. */
export const STAGE_COLORS = TAG_COLORS;
export const createPipelineSchema = z.object({
    name: z.string().trim().min(2, "Dê um nome ao funil").max(80),
    isDefault: z.boolean().optional(),
});
export const updatePipelineSchema = createPipelineSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const createStageSchema = z
    .object({
    name: z.string().trim().min(2, "Dê um nome à etapa").max(60),
    position: z.number().int().min(0).max(99).optional(),
    isWon: z.boolean().optional(),
    isLost: z.boolean().optional(),
    color: tagColorSchema.nullable().optional(),
    /** Horas paradas até o card ser sinalizado. `null` desliga o aviso. */
    stalledAfterHours: z.number().int().min(1).max(8760).nullable().optional(),
})
    .refine((v) => !(v.isWon && v.isLost), {
    message: "Uma etapa não pode ser de ganho e de perda",
    path: ["isWon"],
});
export const updateStageSchema = z
    .object({
    name: z.string().trim().min(2).max(60).optional(),
    position: z.number().int().min(0).max(99).optional(),
    isWon: z.boolean().optional(),
    isLost: z.boolean().optional(),
    color: tagColorSchema.nullable().optional(),
    stalledAfterHours: z.number().int().min(1).max(8760).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" })
    .refine((v) => !(v.isWon && v.isLost), {
    message: "Uma etapa não pode ser de ganho e de perda",
    path: ["isWon"],
});
export const reorderStagesSchema = z.object({
    /** A lista inteira na ordem desejada — não "mova X para a posição N". */
    stageIds: z.array(z.string().uuid()).min(1).max(50),
});
export const lossReasonsSchema = z.object({
    reasons: z.array(z.string().trim().min(2).max(80)).max(30),
});
// ── Negócios ────────────────────────────────────────────────────────────────
export const DEAL_STATUSES = ["open", "won", "lost"];
export const dealStatusSchema = z.enum(DEAL_STATUSES);
export const DEAL_STATUS_LABEL = {
    open: "Em aberto",
    won: "Ganho",
    lost: "Perdido",
};
export const listDealsQuerySchema = z.object({
    pipelineId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    status: dealStatusSchema.optional(),
    search: z.string().trim().min(1).max(120).optional(),
    createdFrom: z.coerce.date().optional(),
    createdTo: z.coerce.date().optional(),
});
export const updateDealSchema = z
    .object({
    title: z.string().trim().min(2).max(160).optional(),
    /** Valor em reais, com duas casas — igual ao `numeric(12,2)` do banco. */
    value: z.number().min(0).max(99_999_999).optional(),
    ownerId: z.string().uuid().nullable().optional(),
    productId: z.string().uuid().nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const moveDealSchema = z.object({
    stageId: z.string().uuid(),
    /** Ordem dentro da coluna, para o arrastar-e-soltar. */
    position: z.number().int().min(0).max(9999).optional(),
    lossReason: z.string().trim().min(2).max(200).optional(),
});
export const loseDealSchema = z.object({
    reason: z.string().trim().min(2, "Informe o motivo da perda").max(200),
});
// ── Produtos e vendas ───────────────────────────────────────────────────────
export const createProductSchema = z.object({
    name: z.string().trim().min(2, "Informe o nome").max(120),
    description: z.string().trim().max(500).nullable().optional(),
    category: z.string().trim().max(80).nullable().optional(),
    /** Reais com duas casas, como o `numeric(12,2)` do banco. */
    price: z.number().min(0).max(99_999_999),
    cost: z.number().min(0).max(99_999_999).optional(),
    /** Minutos de agenda. `null` = não agendável. */
    durationMinutes: z.number().int().min(1).max(1440).nullable().optional(),
    active: z.boolean().optional(),
});
export const updateProductSchema = createProductSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const PAYMENT_METHODS = [
    "pix",
    "credit_card",
    "debit_card",
    "cash",
    "transfer",
    "installment",
    "other",
];
export const PAYMENT_METHOD_LABEL = {
    pix: "PIX",
    credit_card: "Cartão de crédito",
    debit_card: "Cartão de débito",
    cash: "Dinheiro",
    transfer: "Transferência",
    installment: "Parcelado",
    other: "Outro",
};
export const PAYMENT_STATUSES = ["pending", "paid", "refunded", "cancelled"];
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export const PAYMENT_STATUS_LABEL = {
    pending: "A receber",
    paid: "Paga",
    refunded: "Estornada",
    cancelled: "Cancelada",
};
export const saleItemSchema = z
    .object({
    productId: z.string().uuid().nullable().optional(),
    /** Obrigatório quando não vem do catálogo. */
    productName: z.string().trim().min(2).max(160).optional(),
    quantity: z.number().int().min(1).max(999).default(1),
    unitPrice: z.number().min(0).max(99_999_999).optional(),
    discount: z.number().min(0).max(99_999_999).optional(),
})
    .refine((v) => Boolean(v.productId ?? v.productName), {
    message: "Informe o produto ou o nome do item",
    path: ["productName"],
});
export const createSaleSchema = z.object({
    contactId: z.string().uuid(),
    dealId: z.string().uuid().nullable().optional(),
    items: z.array(saleItemSchema).min(1, "Informe ao menos um item").max(50),
    paymentMethod: z.enum(PAYMENT_METHODS).nullable().optional(),
    /** `paid` já na criação cobre a venda de balcão, que é a maioria. */
    paymentStatus: z.enum(["pending", "paid"]).optional(),
    soldAt: z.coerce.date().optional(),
});
export const listSalesQuerySchema = paginationSchema.extend({
    paymentStatus: paymentStatusSchema.optional(),
    contactId: z.string().uuid().optional(),
});
// ── Integrações e mensageria ────────────────────────────────────────────────
export const INTEGRATION_PROVIDERS = [
    "whatsapp_cloud",
    "whatsapp_evolution",
    "meta_ads",
    "meta_lead_ads",
    "meta_social",
    "openai",
    "anthropic",
    "gemini",
];
export const integrationProviderSchema = z.enum(INTEGRATION_PROVIDERS);
export const INTEGRATION_LABEL = {
    whatsapp_cloud: "WhatsApp oficial (Cloud API)",
    whatsapp_evolution: "WhatsApp não oficial (Evolution)",
    meta_ads: "Meta Ads",
    meta_lead_ads: "Meta Lead Ads",
    meta_social: "Facebook e Instagram (comentários e direct)",
    openai: "OpenAI (transcrição, rascunho e agente dos fluxos)",
    anthropic: "Anthropic Claude (agente de IA dos fluxos)",
    gemini: "Google Gemini (agente de IA dos fluxos)",
};
/**
 * O aviso que não pode ficar implícito.
 *
 * A Evolution não recebe o objeto `referral` que a Meta anexa à primeira
 * mensagem vinda de um anúncio. Campanha atendida por ali fica SEM atribuição
 * de anúncio — e sem esse aviso o gestor conclui que o tracking quebrou.
 */
export const INTEGRATION_CAVEAT = {
    whatsapp_evolution: "Conversa por este canal não tem atribuição de anúncio: a API não oficial não recebe o ctwa_clid. Para medir Click-to-WhatsApp, use o canal oficial.",
};
/** Campos de configuração por provedor — a tela monta o formulário daqui. */
export const INTEGRATION_FIELDS = {
    whatsapp_cloud: [
        { key: "phoneNumberId", label: "Phone number ID", hint: "Encontrado no painel da Meta" },
        { key: "wabaId", label: "WhatsApp Business Account ID" },
        { key: "access_token", label: "Token de acesso", secret: true },
    ],
    whatsapp_evolution: [
        { key: "baseUrl", label: "URL da Evolution", hint: "https://evolution.seudominio.com" },
        { key: "instance", label: "Nome da instância" },
        { key: "api_key", label: "Chave da API", secret: true },
        {
            key: "readOnly",
            label: "Somente escuta — não envia nada por este canal",
            type: "boolean",
            hint: "Recebe e conta os leads, mas bloqueia envio, inclusive por automação. Um canal não oficial pode ser banido por disparo indevido, e o bloqueio impede o disparo acidental.",
        },
    ],
    openai: [
        { key: "api_key", label: "Chave da API", secret: true, hint: "Começa com sk-" },
        {
            key: "transcriptionModel",
            label: "Modelo de transcrição",
            hint: "Padrão: whisper-1. É o que lê os áudios da conversa",
        },
        {
            key: "draftModel",
            label: "Modelo do rascunho",
            hint: "Padrão: gpt-4o-mini. Escreve a sugestão que o atendente aprova",
        },
        {
            key: "chatModel",
            label: "Modelo do agente dos fluxos",
            hint: "Padrão: o modelo do rascunho. Conversa com a pessoa no bloco de IA",
        },
        {
            key: "flowsDefault",
            label: "Provedor padrão do agente de IA dos fluxos",
            type: "boolean",
            hint: "Usado pelos blocos de IA que não escolheram provedor. Sem nenhum marcado, vale o primeiro com chave: Anthropic, OpenAI, Gemini.",
        },
    ],
    anthropic: [
        {
            key: "api_key",
            label: "Chave da API",
            secret: true,
            hint: "Começa com sk-ant-. Gere em console.anthropic.com › API Keys",
        },
        {
            key: "chatModel",
            label: "Modelo do agente dos fluxos",
            hint: "Padrão: claude-opus-5-5",
        },
        {
            key: "flowsDefault",
            label: "Provedor padrão do agente de IA dos fluxos",
            type: "boolean",
            hint: "Usado pelos blocos de IA que não escolheram provedor. Sem nenhum marcado, vale o primeiro com chave: Anthropic, OpenAI, Gemini.",
        },
    ],
    gemini: [
        {
            key: "api_key",
            label: "Chave da API",
            secret: true,
            hint: "Gere em aistudio.google.com › Get API key",
        },
        {
            key: "chatModel",
            label: "Modelo do agente dos fluxos",
            hint: "Padrão: gemini-2.5-flash",
        },
        {
            key: "flowsDefault",
            label: "Provedor padrão do agente de IA dos fluxos",
            type: "boolean",
            hint: "Usado pelos blocos de IA que não escolheram provedor. Sem nenhum marcado, vale o primeiro com chave: Anthropic, OpenAI, Gemini.",
        },
    ],
    meta_ads: [
        { key: "adAccountId", label: "ID da conta de anúncios", hint: "act_123456789" },
        { key: "access_token", label: "Token de acesso", secret: true },
        {
            key: "pixelId",
            label: "ID do pixel (conjunto de dados)",
            hint: "Gerenciador de Eventos › Fontes de dados. É para lá que vão as conversões do CRM",
        },
        {
            key: "capi_access_token",
            label: "Token da API de Conversões (opcional)",
            secret: true,
            hint: "Gerado no Gerenciador de Eventos para este pixel. Preenchido, vale só para as conversões; vazio, vale o token de acesso acima",
        },
        {
            key: "testEventCode",
            label: "Código de teste (opcional)",
            hint: "Só para conferir em Eventos de teste. Evento de teste também entra na medição — apague depois",
        },
        {
            key: "capiLead",
            label: "Enviar Lead — lead novo (WhatsApp, formulário, comentário)",
            type: "boolean",
            defaultValue: true,
        },
        {
            key: "capiQualified",
            label: "Enviar QualifiedLead — lead marcado como qualificado",
            type: "boolean",
            defaultValue: true,
        },
        {
            key: "capiSchedule",
            label: "Enviar Schedule — agendamento criado (remarcação não conta de novo)",
            type: "boolean",
            defaultValue: true,
        },
        {
            key: "capiAttended",
            label: "Enviar AttendedAppointment — paciente chegou ou foi atendido",
            type: "boolean",
            defaultValue: true,
        },
        {
            key: "capiPurchase",
            label: "Enviar Purchase — venda paga, com valor",
            type: "boolean",
            defaultValue: true,
        },
        {
            key: "capiMessaging",
            label: "Quem veio de anúncio de WhatsApp vai pelo canal de mensagens",
            hint: "LeadSubmitted, QualifiedLead e Purchase com o clique do anúncio e a Página conectada — é o que credita a conversão ao anúncio de WhatsApp",
            type: "boolean",
            defaultValue: true,
        },
    ],
    meta_lead_ads: [
        { key: "pageId", label: "ID da página" },
        { key: "page_access_token", label: "Token da página", secret: true },
    ],
    meta_social: [
        { key: "pageId", label: "ID da página do Facebook" },
        {
            key: "igUserId",
            label: "ID da conta do Instagram",
            hint: "O ID profissional vinculado à página. Deixe em branco se só usa Facebook.",
        },
        {
            key: "page_access_token",
            label: "Token da página",
            secret: true,
            hint: "O MESMO token opera o Instagram: a conta profissional é acessada pela página.",
        },
    ],
};
export const saveIntegrationSchema = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    /** Segredo em branco significa "não mexi", nunca "apague". */
    secrets: z.record(z.string(), z.string()).optional(),
    /**
     * Nome do número para quem atende ("Salvador", "Feira"). Só faz sentido nos
     * provedores de WhatsApp, onde a empresa pode ter mais de um número.
     */
    label: z.string().trim().max(40).nullable().optional(),
});
/**
 * Provedores em que a empresa pode ter VÁRIOS números.
 *
 * Cada número é uma linha própria da integração, com credencial, webhook e
 * estado de sessão próprios. Nos demais provedores continua valendo uma
 * integração por empresa.
 */
export const MULTI_ACCOUNT_PROVIDERS = ["whatsapp_cloud", "whatsapp_evolution"];
export const CONVERSATION_SCOPES = [
    "mine",
    "unassigned",
    "unread",
    /**
     * Ninguém nunca respondeu esta pessoa.
     *
     * Separado de "aguardando resposta" porque são falhas diferentes: aguardando
     * inclui quem já conversou conosco e voltou a escrever; aqui é o silêncio
     * absoluto — alguém procurou a clínica, provavelmente por um anúncio pago, e
     * não recebeu nem a primeira palavra. É a pior linha da caixa de entrada e
     * ficava diluída no meio das outras.
     */
    "pending",
    "waiting",
    "closed",
    "all",
];
export const CONVERSATION_SCOPE_LABEL = {
    mine: "Minhas",
    unassigned: "Sem responsável",
    unread: "Não lidas",
    pending: "Nunca respondidas",
    waiting: "Aguardando resposta",
    closed: "Fechadas",
    all: "Todas",
};
export const listConversationsQuerySchema = paginationSchema.extend({
    scope: z.enum(CONVERSATION_SCOPES).default("mine"),
    search: z.string().trim().min(1).max(120).optional(),
    /** Filtros da central: combinam com o escopo, não o substituem. */
    channel: z.string().trim().max(40).optional(),
    /** Número da empresa por onde a conversa entrou (só com mais de um número). */
    integrationId: z.string().uuid().optional(),
    teamId: z.string().uuid().optional(),
    assigneeId: z.string().uuid().optional(),
    priority: z.enum(["low", "normal", "high", "urgent"]).optional(),
});
export const updateConversationSchema = z
    .object({
    assigneeId: z.string().uuid().nullable().optional(),
    status: z.enum(["open", "pending", "snoozed", "closed"]).optional(),
    snoozedUntil: z.coerce.date().nullable().optional(),
    markRead: z.boolean().optional(),
    teamId: z.string().uuid().nullable().optional(),
    priority: z.enum(["low", "normal", "high", "urgent"]).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const sendMessageSchema = z
    .object({
    text: z.string().trim().min(1).max(4000).optional(),
    templateName: z.string().trim().min(1).max(120).optional(),
    language: z.string().trim().max(10).optional(),
    variables: z.array(z.string().max(500)).max(10).optional(),
    /**
     * Nota interna: entra na conversa e o cliente nunca vê.
     *
     * Vai no MESMO endpoint de envio porque, para quem atende, é o mesmo gesto:
     * digitar e apertar enter. Endpoint separado significaria uma segunda caixa
     * de texto na tela, e a nota acabaria indo para a paciente no dia de correria.
     */
    internal: z.boolean().optional(),
    /** Mídia já subida em /media — o id que aquele endpoint devolveu. */
    mediaId: z.string().uuid().optional(),
    caption: z.string().trim().max(1024).optional(),
})
    .refine((v) => Boolean(v.text ?? v.templateName ?? v.mediaId), {
    message: "Escreva a mensagem ou escolha um template",
    path: ["text"],
});
// ── Rótulos em português para a interface ───────────────────────────────────
/**
 * O que a paciente quis na última mensagem, lido automaticamente.
 *
 * Os valores vêm do detector em `@rn/core`; o rótulo mora aqui porque é
 * apresentação, e porque a tela só enxerga este pacote.
 */
export const LEAD_INTENTS = ["preco", "agendar", "local", "procedimento", "objecao"];
export const INTENT_LABEL = {
    preco: "Perguntou preço",
    agendar: "Quer agendar",
    local: "Perguntou endereço",
    procedimento: "Dúvida do procedimento",
    objecao: "Achou caro",
};
export const LEAD_STATUS_LABEL = {
    new: "Novo",
    working: "Em atendimento",
    qualified: "Qualificado",
    unqualified: "Fora do perfil",
    converted: "Convertido",
    lost: "Perdido",
};
export const LEAD_TEMPERATURE_LABEL = {
    cold: "Frio",
    warm: "Morno",
    hot: "Quente",
    very_hot: "Muito quente",
};
export const APP_ROLE_LABEL = {
    super_admin: "Super administrador",
    admin: "Administrador",
    manager: "Gestor",
    sales: "Vendedor",
    reception: "Recepção",
    professional: "Profissional",
};
// ── Agenda ──────────────────────────────────────────────────────────────────
export const APPOINTMENT_STATUSES = [
    "scheduled",
    "confirmed",
    "completed",
    "cancelled",
    "no_show",
    "rescheduled",
];
export const APPOINTMENT_STATUS_LABEL = {
    scheduled: "Agendado",
    confirmed: "Confirmado",
    completed: "Atendido",
    cancelled: "Cancelado",
    no_show: "Não compareceu",
    rescheduled: "Remarcado",
};
/** Data no calendário, no fuso do tenant — nunca um instante UTC. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use o formato AAAA-MM-DD");
/** Hora do relógio da clínica. Guardada como texto pelo mesmo motivo. */
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use o formato HH:MM");
export const createProfessionalSchema = z.object({
    name: z.string().trim().min(2).max(120),
    specialties: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, "Use uma cor no formato #RRGGBB")
        .nullable()
        .optional(),
    userId: z.string().uuid().nullable().optional(),
});
export const updateProfessionalSchema = createProfessionalSchema
    .partial()
    .extend({ active: z.boolean().optional() })
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const createLocationSchema = z.object({
    name: z.string().trim().min(2).max(120),
    capacity: z.coerce.number().int().min(1).max(100).default(1),
});
export const createAvailabilitySchema = z
    .object({
    weekday: z.coerce.number().int().min(0).max(6),
    startTime: clockTime,
    endTime: clockTime,
    specificDate: isoDate.nullable().optional(),
    isBlock: z.boolean().default(false),
    reason: z.string().trim().max(200).nullable().optional(),
})
    .refine((v) => v.startTime < v.endTime, {
    message: "O fim precisa ser depois do começo",
    path: ["endTime"],
});
export const slotsQuerySchema = z.object({
    date: isoDate,
    durationMinutes: z.coerce.number().int().min(5).max(600).default(60),
    stepMinutes: z.coerce.number().int().min(5).max(240).default(30),
});
export const createAppointmentSchema = z.object({
    contactId: z.string().uuid(),
    professionalId: z.string().uuid().nullable().optional(),
    locationId: z.string().uuid().nullable().optional(),
    productId: z.string().uuid().nullable().optional(),
    leadId: z.string().uuid().nullable().optional(),
    dealId: z.string().uuid().nullable().optional(),
    title: z.string().trim().min(2).max(160).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    scheduledAt: z.coerce.date(),
    durationMinutes: z.coerce.number().int().min(5).max(600).optional(),
    price: z.coerce.number().min(0).max(9_999_999).nullable().optional(),
});
/**
 * Agendamentos de um intervalo.
 *
 * Duas formas: um período (`preset`, com `from`/`to` como DIAS no
 * personalizado) resolvido no fuso da clínica — o que a agenda e o "hoje" do
 * atendente usam —, ou `from`/`to` como instantes, o formato antigo. O antigo
 * continua aceito; o problema dele era o limite do dia sair do fuso do
 * navegador, e "hoje" começar às 21h de ontem.
 */
export const listAppointmentsQuerySchema = z.object({
    preset: z.enum(PERIOD_PRESETS).optional(),
    from: z.string().trim().min(1).max(40).optional(),
    to: z.string().trim().min(1).max(40).optional(),
    professionalId: z.string().uuid().optional(),
});
export const APPOINTMENT_TRANSITIONS = [
    "confirmed",
    "arrived",
    "completed",
    "cancelled",
    "no_show",
];
export const transitionAppointmentSchema = z.object({
    to: z.enum(APPOINTMENT_TRANSITIONS),
    reason: z.string().trim().max(300).optional(),
});
export const rescheduleAppointmentSchema = z.object({
    scheduledAt: z.coerce.date(),
});
/**
 * Motivos de cancelamento sugeridos.
 *
 * A clínica precisa saber se cancelou por preço ou por agenda — os dois pedem
 * ações opostas. Texto livre também é aceito; a lista só evita que cada
 * recepcionista invente a própria grafia.
 */
export const CANCELLATION_REASON_SUGGESTIONS = [
    "Imprevisto do cliente",
    "Remarcou por telefone",
    "Achou caro",
    "Desistiu do procedimento",
    "Profissional indisponível",
    "Não conseguiu contato",
];
export const requestConfirmationSchema = z.object({
    templateName: z.string().trim().min(1).max(120),
    language: z.string().trim().max(10).default("pt_BR"),
    /** Quando ausente, a API preenche com nome, data/hora e profissional. */
    variables: z.array(z.string().max(500)).max(10).optional(),
});
export const slotsByProfessionalQuerySchema = slotsQuerySchema.extend({
    professionalId: z.string().uuid(),
});
// ── Automação ───────────────────────────────────────────────────────────────
/**
 * Eventos que servem de gatilho.
 *
 * Lista fechada de propósito: o campo é texto no banco para não exigir
 * migração a cada evento novo, mas oferecer texto livre na tela produziria
 * workflows que nunca disparam porque alguém digitou "lead criado".
 */
export const TRIGGER_EVENTS = [
    { value: "lead.created", label: "Lead novo chegou" },
    { value: "lead.status_changed", label: "Status do lead mudou" },
    { value: "lead.assigned", label: "Lead foi atribuído" },
    { value: "message.received", label: "Cliente mandou mensagem" },
    // Separado de "mandou mensagem" porque a resposta é PÚBLICA: um gatilho só
    // faria a automação escrever preço embaixo do post, à vista do concorrente.
    { value: "comment.received", label: "Comentou num post (Facebook/Instagram)" },
    { value: "message.sent", label: "Mensagem foi enviada" },
    { value: "deal.stage_changed", label: "Negócio mudou de etapa" },
    { value: "deal.won", label: "Negócio ganho" },
    { value: "deal.lost", label: "Negócio perdido" },
    { value: "sale.paid", label: "Venda paga" },
    { value: "appointment.created", label: "Agendamento criado" },
    { value: "appointment.confirmed", label: "Agendamento confirmado" },
    { value: "appointment.completed", label: "Atendimento concluído" },
    { value: "appointment.no_show", label: "Cliente não compareceu" },
    { value: "appointment.reschedule_requested", label: "Cliente pediu para remarcar" },
    { value: "task.overdue", label: "Tarefa venceu" },
    { value: "lead.sla_breached", label: "SLA de resposta estourou" },
    { value: "tag.added", label: "Etiqueta foi adicionada" },
    // Fluxos: Typechat, formulário e quiz. O payload traz `leadId`/`contactId`
    // (quando a pessoa já se identificou), `flowId`, `kind` e, na conclusão, as
    // variáveis respondidas.
    { value: "flow.started", label: "Começou um formulário, quiz ou Typechat" },
    { value: "flow.completed", label: "Concluiu um formulário, quiz ou Typechat" },
    { value: "flow.abandoned", label: "Parou no meio de um formulário, quiz ou Typechat" },
    // Checkout, pagamento e acesso: chegam de uma origem externa pela API de
    // eventos (`POST /v1/events`). O payload traz o pedido em `order` — oferta,
    // valor, Pix copia e cola e o link para retomar o checkout.
    { value: "checkout.started", label: "Começou o checkout" },
    { value: "checkout.abandoned", label: "Abandonou o checkout" },
    { value: "payment.pending", label: "Pagamento pendente (Pix ou boleto gerado)" },
    { value: "payment.failed", label: "Pagamento recusado" },
    { value: "sale.upsell_paid", label: "Comprou o upsell" },
    { value: "sale.refunded", label: "Venda reembolsada ou com chargeback" },
    { value: "subscription.renewed", label: "Assinatura renovada" },
    { value: "subscription.canceled", label: "Assinatura cancelada" },
    { value: "subscription.payment_failed", label: "Cobrança da assinatura falhou" },
    { value: "access.granted", label: "Acesso ao produto liberado" },
];
/**
 * Campos que uma condição consegue ler num gatilho de conversa.
 *
 * São OUTROS campos porque a condição é avaliada contra o payload do evento, e
 * o payload de "comentou num post" não tem origem nem campanha — tem post,
 * texto e canal. Oferecer a lista de lead aqui produziria workflows que nunca
 * disparam, que é o modo mais caro de errar: parece configurado e não é.
 */
export const CONVERSATION_TRIGGER_FIELDS = [
    { value: "text", label: "Texto da mensagem/comentário" },
    { value: "postId", label: "Post (id)" },
    { value: "channel", label: "Canal" },
    { value: "authorName", label: "Nome de quem escreveu" },
    { value: "parentId", label: "É resposta a outro comentário" },
];
/** Gatilhos cujo payload é conversa, e não lead. */
export const CONVERSATION_TRIGGERS = ["comment.received", "message.received"];
export const AUTOMATION_STEP_LABEL = {
    send_message: "Enviar mensagem",
    reply_comment: "Responder o comentário (público)",
    send_private_reply: "Responder no direct (privado)",
    add_tag: "Adicionar etiqueta",
    remove_tag: "Remover etiqueta",
    change_stage: "Mover no funil",
    assign: "Atribuir responsável",
    create_task: "Criar tarefa",
    update_lead: "Atualizar lead",
    add_score: "Somar pontos",
    webhook: "Chamar webhook",
    wait: "Esperar",
    stop_if: "Parar se",
    start_typechat: "Iniciar Typechat",
    append_sheet_row: "Adicionar linha na planilha",
};
/**
 * Passo do workflow.
 *
 * Validado como união discriminada e não como objeto solto: um passo
 * `send_message` sem texto nem template é um passo que nunca faz nada, e
 * descobrir isso só quando o lead não recebeu a mensagem é tarde demais.
 */
export const automationStepSchema = z.discriminatedUnion("type", [
    // A checagem de "texto OU template" nao mora aqui: `discriminatedUnion` so
    // aceita objetos puros, e um `.refine` transformaria este ramo num
    // ZodEffects. Fica no `superRefine` da lista de passos, logo abaixo.
    z.object({
        type: z.literal("send_message"),
        text: z.string().trim().max(4000).optional(),
        templateName: z.string().trim().max(120).optional(),
        language: z.string().trim().max(10).optional(),
        variables: z.array(z.string().max(500)).max(10).optional(),
        /** Só envia para quem deu consentimento de WhatsApp. */
        requireConsent: z.boolean().optional(),
    }),
    // A Meta corta comentário em 8.000 caracteres, mas o limite prático é bem
    // menor: resposta longa embaixo de post não é lida. 1.000 é folgado o
    // bastante e evita o erro de campo vindo da API.
    z.object({
        type: z.literal("reply_comment"),
        text: z.string().trim().min(1, "Escreva o texto da resposta").max(1000),
    }),
    z.object({
        type: z.literal("send_private_reply"),
        text: z.string().trim().min(1, "Escreva o texto da resposta").max(1000),
    }),
    z.object({ type: z.literal("add_tag"), tagName: z.string().trim().min(1).max(40) }),
    z.object({ type: z.literal("remove_tag"), tagName: z.string().trim().min(1).max(40) }),
    z.object({ type: z.literal("change_stage"), stageName: z.string().trim().min(1).max(80) }),
    z.object({
        type: z.literal("assign"),
        userId: z.string().uuid().optional(),
        strategy: z.literal("routing").optional(),
    }),
    z.object({
        type: z.literal("create_task"),
        title: z.string().trim().min(2).max(160),
        dueInMinutes: z.number().int().min(1).max(525_600).optional(),
        assigneeId: z.string().uuid().optional(),
    }),
    z.object({
        type: z.literal("update_lead"),
        status: leadStatusSchema.optional(),
        temperature: leadTemperatureSchema.optional(),
    }),
    z.object({ type: z.literal("add_score"), points: z.number().int().min(-100).max(100) }),
    z.object({
        type: z.literal("webhook"),
        // https obrigatório: http levaria o dado do lead em texto claro para fora.
        url: z.string().url().startsWith("https://", "A URL precisa usar https://").max(500),
        method: z.enum(["GET", "POST"]).optional(),
        headers: z.record(z.string(), z.string()).optional(),
    }),
    z.object({
        type: z.literal("wait"),
        minutes: z.number().int().min(1).max(525_600),
        businessHoursOnly: z.boolean().optional(),
    }),
    z.object({
        type: z.literal("stop_if"),
        conditions: z.array(routingConditionSchema).min(1).max(10),
    }),
    // Põe a conversa de WhatsApp do contato num Typechat publicado.
    z.object({ type: z.literal("start_typechat"), flowId: z.string().uuid("Escolha o Typechat") }),
    // Google Planilhas. O id vem já extraído da URL pela tela; aqui só se
    // confere o formato, para a regra não ser salva apontando para lixo.
    z.object({
        type: z.literal("append_sheet_row"),
        spreadsheetId: z
            .string()
            .trim()
            .regex(/^[A-Za-z0-9_-]{20,}$/, "Cole a URL ou o id da planilha"),
        sheetName: z.string().trim().min(1, "Informe a aba").max(100),
        columns: z.array(sheetColumnSchema).min(1, "Adicione ao menos uma coluna").max(50),
    }),
]);
export const createAutomationSchema = z.object({
    name: z.string().trim().min(2, "Dê um nome ao workflow").max(120),
    description: z.string().trim().max(500).nullable().optional(),
    triggerEvent: z.string().trim().min(3).max(80),
    conditions: z.array(routingConditionSchema).max(10).default([]),
    steps: z
        .array(automationStepSchema)
        .min(1, "Adicione ao menos um passo")
        .max(30)
        .superRefine((steps, ctx) => {
        steps.forEach((step, index) => {
            // Um passo de mensagem sem texto nem template e um passo que nunca faz
            // nada, e descobrir isso quando o lead nao recebeu e tarde demais.
            if (step.type === "send_message" && !step.text && !step.templateName) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, "text"],
                    message: "Escreva a mensagem ou escolha um template",
                });
            }
        });
    }),
    /**
     * Sem cooldown, um workflow disparado por "mensagem recebida" abre uma
     * execução por mensagem e a régua vira enxurrada. Uma hora é o padrão
     * conservador; quem quiser sem limite informa `null` de propósito.
     */
    cooldownSeconds: z.number().int().min(0).max(2_592_000).nullable().default(3600),
    maxRunsPerEntity: z.number().int().min(1).max(100).nullable().default(null),
    active: z.boolean().default(false),
});
export const updateAutomationSchema = createAutomationSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
export const testAutomationSchema = z.object({
    /** Lead sobre o qual simular. Sem ele, o teste só avalia as condições. */
    leadId: z.string().uuid().optional(),
    payload: z.record(z.string(), z.unknown()).default({}),
});
export const listAutomationRunsQuerySchema = paginationSchema.extend({
    workflowId: z.string().uuid().optional(),
    status: z.enum(["pending", "running", "waiting", "completed", "failed"]).optional(),
});
export const AUTOMATION_RUN_STATUS_LABEL = {
    pending: "Na fila",
    running: "Executando",
    waiting: "Esperando",
    completed: "Concluída",
    failed: "Falhou",
};
// ── SLA ─────────────────────────────────────────────────────────────────────
export const createSlaPolicySchema = z.object({
    name: z.string().trim().min(2, "Dê um nome à política").max(80),
    /** Meta de primeira resposta. 15 minutos é o padrão de captação por anúncio. */
    firstResponseTargetSeconds: z.coerce.number().int().min(60).max(604_800),
    /**
     * Quando avisar antes de estourar. Sem valor, o aviso sai a 75% da meta —
     * cedo o bastante para dar tempo de responder, tarde o bastante para não
     * virar ruído.
     */
    breachWarningSeconds: z.coerce.number().int().min(0).max(604_800).nullable().optional(),
    /**
     * Um lead que entra 22h de sábado não deve contar contra o tempo de
     * resposta: a equipe seria cobrada por horas em que ninguém trabalhava.
     */
    businessHoursOnly: z.boolean().default(true),
    conditions: z.array(routingConditionSchema).max(10).default([]),
});
export const updateSlaPolicySchema = createSlaPolicySchema
    .partial()
    .extend({ active: z.boolean().optional() })
    .refine((v) => Object.keys(v).length > 0, { message: "Informe ao menos um campo" });
// ── Notificações ────────────────────────────────────────────────────────────
export const NOTIFICATION_TYPE_LABEL = {
    "lead.assigned": "Lead novo",
    "message.received": "Mensagem",
    "lead.sla_at_risk": "SLA em risco",
    "lead.sla_breached": "SLA estourado",
    "task.overdue": "Tarefa vencida",
    "sale.paid": "Venda paga",
};
// ── Relatórios ──────────────────────────────────────────────────────────────
/**
 * Período dos relatórios: o mesmo esquema de toda tela (atalho, dias do
 * personalizado ou o `days` antigo), resolvido no fuso da clínica.
 */
export const analyticsQuerySchema = periodQuerySchema;
/** Níveis da mídia em que o resultado real pode ser aberto. */
export const MEDIA_LEVELS = ["campaign", "adset", "ad"];
export const MEDIA_LEVEL_LABEL = {
    campaign: "Campanha",
    adset: "Conjunto",
    ad: "Anúncio",
};
/**
 * Resultado real por campanha, conjunto ou anúncio.
 *
 * `campaign` restringe a uma campanha (id externo) — é o "abrir a campanha"
 * da tela, para ver qual conjunto ou criativo trouxe o paciente.
 */
export const mediaBreakdownQuerySchema = analyticsQuerySchema.extend({
    level: z.enum(MEDIA_LEVELS).default("campaign"),
    campaign: z.string().trim().max(64).optional(),
});
export const ATTRIBUTION_MODELS = [
    "first_touch",
    "last_touch",
    "linear",
    "position_based",
    "time_decay",
];
export const ATTRIBUTION_MODEL_LABEL = {
    first_touch: "Primeiro toque",
    last_touch: "Último toque",
    linear: "Linear",
    position_based: "Posicional (40/20/40)",
    time_decay: "Decaimento no tempo",
};
export const attributionQuerySchema = analyticsQuerySchema.extend({
    model: z.enum(ATTRIBUTION_MODELS).default("last_touch"),
});
export const PERIOD_OPTIONS = [
    { value: 7, label: "7 dias" },
    { value: 14, label: "14 dias" },
    { value: 30, label: "30 dias" },
    { value: 90, label: "90 dias" },
];
export const EXPORT_ENTITIES = ["leads", "contacts", "sales", "appointments"];
export const EXPORT_ENTITY_LABEL = {
    leads: "Leads",
    contacts: "Contatos",
    sales: "Vendas",
    appointments: "Agendamentos",
};
export const exportQuerySchema = z.object({
    entity: z.enum(EXPORT_ENTITIES),
    /** `csv` abre direto no Excel; `xlsx` preserva tipo de número e data. */
    format: z.enum(["csv", "xlsx"]).default("csv"),
    /** Sem período nenhum, os últimos 90 dias — o padrão de antes. */
    ...periodQueryFields,
});
// ── Tracking ────────────────────────────────────────────────────────────────
const trackingId = z.string().trim().min(8).max(120);
export const trackSchema = z.object({
    anonymousId: trackingId,
    sessionId: trackingId,
    url: z.string().trim().max(1000).nullable().optional(),
    referrer: z.string().trim().max(1000).nullable().optional(),
    utmSource: z.string().trim().max(200).nullable().optional(),
    utmMedium: z.string().trim().max(200).nullable().optional(),
    utmCampaign: z.string().trim().max(200).nullable().optional(),
    utmContent: z.string().trim().max(200).nullable().optional(),
    utmTerm: z.string().trim().max(200).nullable().optional(),
    fbclid: z.string().trim().max(500).nullable().optional(),
    fbc: z.string().trim().max(500).nullable().optional(),
    fbp: z.string().trim().max(500).nullable().optional(),
    gclid: z.string().trim().max(500).nullable().optional(),
    consent: z.boolean().default(false),
});
export const identifySchema = z
    .object({
    anonymousId: trackingId,
    phone: z.string().trim().max(30).nullable().optional(),
    email: z.string().trim().max(200).nullable().optional(),
})
    .refine((v) => Boolean(v.phone ?? v.email), {
    message: "Informe telefone ou e-mail",
    path: ["phone"],
});
// ── Conversões ──────────────────────────────────────────────────────────────
export const CONVERSION_STATUS_LABEL = {
    pending: "Na fila",
    sent: "Enviado",
    failed: "Falhou",
    skipped: "Ignorado",
};
export const listConversionsQuerySchema = paginationSchema.extend({
    status: z.enum(["pending", "sent", "failed", "skipped"]).optional(),
    /** Período pelo momento da conversão, no fuso da clínica. Sem ele, todas. */
    ...periodQueryFields,
});
// ── LGPD ────────────────────────────────────────────────────────────────────
export const DATA_SUBJECT_REQUEST_TYPES = [
    "access",
    "portability",
    "deletion",
    "anonymization",
];
export const DATA_SUBJECT_TYPE_LABEL = {
    access: "Acesso aos dados",
    portability: "Portabilidade",
    deletion: "Exclusão",
    anonymization: "Anonimização",
};
export const createDataRequestSchema = z.object({
    contactId: z.string().uuid(),
    type: z.enum(DATA_SUBJECT_REQUEST_TYPES),
    requestedBy: z.string().trim().max(200).optional(),
    notes: z.string().trim().max(1000).optional(),
});
export * from "./attendance";
export * from "./flows";
export * from "./knowledge";
export * from "./google";
export * from "./events-api";
export * from "./platform";
//# sourceMappingURL=index.js.map